/**
 * Game rules: tray, placement, formation clears, scoring, game over.
 *
 * The board is the pyramid surface from geometry.js; the clearable formations
 * and their values come from formations.js. Pieces have one fixed orientation
 * and there is no rotation control, so a tray entry is just a piece key.
 */

import { CELL_COUNT } from './geometry.js';
import { findClears, formationsCompletedBy, scoreClears } from './formations.js';
import { randomTone } from './palette.js';
import { PIECES, PIECES_BY_KEY, realizeOnSurface } from './pieces.js';

export { findClears, formationsCompletedBy };

export const TRAY_SIZE = 3;

/** Points per cell just for placing, so a quiet move still registers. */
const PLACE_POINTS = 5;

/** Relative frequency of each piece in the deal. */
const WEIGHTS = {
  up: 4,
  down: 4,
  'rhombus-flat': 3,
  'rhombus-right': 3,
  'rhombus-left': 3,
  'bar-c-up': 2,
  'bar-c-down': 2,
  'bar-b-up': 2,
  'bar-b-down': 2,
  'bar-a-up': 2,
  'bar-a-down': 2,
};

const WEIGHTED_KEYS = PIECES.flatMap((p) => Array(WEIGHTS[p.key] ?? 1).fill(p.key));

export function createState() {
  const state = {
    filled: new Set(),
    /** cell id -> palette index of the stone sitting there */
    tones: new Map(),
    tray: [],
    score: 0,
    clears: 0,
    best: 0,
    over: false,
  };
  deal(state);
  return state;
}

function randomPiece() {
  const key = WEIGHTED_KEYS[Math.floor(Math.random() * WEIGHTED_KEYS.length)];
  // A piece is dealt its colour and keeps it: the stones you drop stay the
  // colour they were in your hand.
  return { key, tone: randomTone(), uid: `${key}-${Math.random().toString(36).slice(2, 8)}` };
}

/** Fill the tray. All three must be spent before the next batch arrives. */
export function deal(state) {
  state.tray = Array.from({ length: TRAY_SIZE }, randomPiece);
}

/** Cells a tray piece would occupy if dropped on `anchorId`, else null. */
export function previewPlacement(state, slot, anchorId) {
  const entry = state.tray[slot];
  if (!entry || anchorId == null) return null;

  const cells = realizeOnSurface(anchorId, PIECES_BY_KEY.get(entry.key));
  if (!cells) return null;
  if (cells.some((id) => state.filled.has(id))) return null;
  return cells;
}

/**
 * Drop a piece. Returns a result describing what happened, or null if the
 * placement was illegal.
 *
 * @returns {{
 *   placed: number[], cleared: number[], clears: object[], gained: number,
 *   combo: number, multiplier: number, over: boolean
 * } | null}
 */
export function placePiece(state, slot, anchorId) {
  if (state.over) return null;
  const placed = previewPlacement(state, slot, anchorId);
  if (!placed) return null;

  const tone = state.tray[slot].tone ?? 0;
  for (const id of placed) {
    state.filled.add(id);
    state.tones?.set(id, tone);
  }
  state.tray[slot] = null;

  const clears = findClears(state.filled);
  const cleared = [...new Set(clears.flatMap((c) => c.cells))];
  for (const id of cleared) {
    state.filled.delete(id);
    state.tones?.delete(id);
  }

  const { points, multiplier } = scoreClears(clears);
  const gained = placed.length * PLACE_POINTS + points;

  state.score += gained;
  state.clears += clears.length;
  state.best = Math.max(state.best, points);

  if (state.tray.every((entry) => !entry)) deal(state);
  state.over = !hasLegalMove(state);

  return {
    placed,
    cleared,
    clears,
    gained,
    combo: clears.length,
    multiplier,
    over: state.over,
  };
}

/** Can this piece be placed anywhere — on any cell of any of the three faces? */
export function canPlaceKey(state, key) {
  const piece = PIECES_BY_KEY.get(key);
  for (let id = 0; id < CELL_COUNT; id += 1) {
    if (state.filled.has(id)) continue;
    const cells = realizeOnSurface(id, piece);
    if (cells && !cells.some((c) => state.filled.has(c))) return true;
  }
  return false;
}

/**
 * Game over only when no tray piece fits anywhere. The scan is the whole
 * pyramid — every cell of every face, never just the face turned to the
 * camera — so a fit the player cannot currently see still keeps the run
 * alive.
 */
export function hasLegalMove(state) {
  return state.tray.some((entry) => entry && canPlaceKey(state, entry.key));
}

/** Which tray slots are dead ends, for dimming them in the UI. */
export function deadSlots(state) {
  return state.tray.map((entry) => !!entry && !canPlaceKey(state, entry.key));
}
